import { useState } from 'react';
import type { RecordEmbedBlock } from '@netsuite-ai/types';
import { openRecord } from '../../lib/host-bridge';
import { useAuthStore } from '../../store/auth.store';
import { apiFetch } from '../../lib/api';

interface Props {
  block: RecordEmbedBlock;
}

const VISIBLE_FIELDS = 5;

export function RecordEmbed({ block }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(block.fields).map(([k, v]) => [k, String(v ?? '')]),
    ),
  );
  const [savedFields, setSavedFields] = useState(editedFields);

  const allFields = Object.entries(savedFields);
  const visibleFields = showAll
    ? allFields
    : allFields.slice(0, VISIBLE_FIELDS);
  const hiddenCount = allFields.length - VISIBLE_FIELDS;

  const recordName = savedFields['Name'];

  // inside the component:
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'support_readonly';

  function handleEdit() {
    setEditedFields({ ...savedFields });
    setIsEditing(true);
  }

  function handleCancel() {
    setEditedFields({ ...savedFields });
    setIsEditing(false);
  }

  async function handleSave() {
    setSavedFields({ ...editedFields });
    setIsEditing(false);
    await apiFetch(`/api/chat/blocks/${block.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ fields: editedFields }),
    });
  }

  function formatName(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  if (isEditing) {
    return (
      <div className="my-2 overflow-hidden rounded border border-neutral-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-neutral-100 px-4 py-3">
          <span className="bg-ink/14 rounded-full border border-neutral-300 px-2.5 py-0.5 text-xs font-medium text-neutral-600">
            {formatName(block.recordType)}
          </span>
          <span className="text-sm text-neutral-500">{block.recordId}</span>
          {recordName && recordName !== block.recordId && (
            <span className="text-sm font-semibold text-neutral-900">
              {recordName}
            </span>
          )}
        </div>

        <div className="divide-y divide-neutral-100 px-4 py-2">
          {Object.keys(editedFields).map((key) => {
            const isDate = /date|at$/i.test(key);
            return (
              <div key={key} className="py-2.5">
                <p className="mb-1 text-sm font-medium text-neutral-900">
                  {key}
                </p>
                <input
                  type={isDate ? 'date' : 'text'}
                  value={editedFields[key]}
                  onChange={(e) =>
                    setEditedFields((f) => ({ ...f, [key]: e.target.value }))
                  }
                  className="w-full rounded border border-neutral-300 px-3 py-1.5 text-sm text-neutral-900 outline-none focus:border-neutral-500"
                />
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 border-t border-neutral-100 bg-neutral-100 px-4 py-6">
          <button
            onClick={handleCancel}
            className="bg-danger rounded px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-ink/14 rounded border border-neutral-300 px-4 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-2 overflow-hidden rounded border border-neutral-200 bg-white shadow-sm">
      {/* Header */}
      <button
        onClick={() => openRecord(block.recordId, block.recordType)}
        className="flex w-full items-center gap-2 border-b border-neutral-100 px-4 py-3 text-left hover:bg-neutral-50"
      >
        <span className="bg-ink/14 rounded-full border border-neutral-300 px-2.5 py-0.5 text-xs font-medium text-neutral-600">
          {formatName(block.recordType)}
        </span>
        <span className="text-sm text-neutral-500">{block.recordId}</span>
        <span className="text-sm font-semibold text-neutral-900">
          {recordName}
        </span>
      </button>

      {/* Field rows */}
      <div className="divide-y divide-neutral-100">
        {visibleFields.map(([key, value]) => (
          <div key={key} className="px-4 py-3">
            <p className="text-sm font-medium text-neutral-900">
              {value || '—'}
            </p>
            <p className="text-sm text-neutral-500">{key}</p>
          </div>
        ))}
      </div>

      {!showAll && hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="w-full border-t border-neutral-100 bg-neutral-50 py-3 text-center text-sm font-semibold text-neutral-700 hover:bg-neutral-100"
        >
          Show {hiddenCount} more fields
        </button>
      )}

      {block.editable && !isReadOnly && (
        <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-2.5">
          <button
            onClick={handleEdit}
            className="text-sm font-medium text-neutral-700 hover:text-neutral-900"
          >
            Edit record
          </button>
        </div>
      )}
    </div>
  );
}
